import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Empty, Badge, Divider, Spin } from 'antd'
import moment from 'moment'
import LangContext from 'modules/shared/context/langContext'
import styled from 'styled-components'

const EVENT_TYPES = [
  {
    key: 'parking',
    color: '#c91c1c',
    getLabel: i18n => i18n.p.parkingOnRoadSide,
  },
  {
    key: 'speed_over',
    color: '#00BF06',
    getLabel: i18n => i18n.s.speedOverLimit,
  },
  {
    key: 'driving_4hr',
    color: '#ffd619',
    getLabel: i18n => i18n.d.driveContinuouslyForMoreThan4Hours,
  },
  {
    key: 'driving_10hr',
    color: '#848484',
    getLabel: i18n => i18n.d.driveContinuouslyForMoreThan10HoursDay,
  },
  {
    key: 'aggressive',
    color: '#722ed1',
    getLabel: i18n => i18n.a.aggressiveAccelerationAndImmediatelyBreak,
  },
]

const SWIPE_THRESHOLD = 50

const Container = styled.div`
  width: 300px;
  padding: 10px 0 4px;
  overflow: hidden;
  position: relative;
`

const Title = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1890ff;
  padding: 0 14px 8px;
`

const SlideWrapper = styled.div`
  display: flex;
  width: 200%;
  transform: translateX(${props => (props.showDetail ? '-50%' : '0')});
  transition: transform 0.3s ease;
`

const ListView = styled.div`
  width: 50%;
  height: 100%;
  overflow-y: auto;
`

const DetailView = styled.div`
  width: 50%;
  height: 100%;
  overflow-y: auto;
`

const SwipeRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 14px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;

  &:hover {
    background: #fafafa;
  }

  &:last-child {
    border-bottom: none;
  }
`

const RowContent = styled.div`
  flex: 1;
  min-width: 0;
`

const EventLabelRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  color: #222;
`

const NewDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #ff4d6a;
  margin-left: 6px;
  flex-shrink: 0;
`

const PreviewText = styled.div`
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BadgeWrapper = styled.div`
  margin-left: 8px;
  flex-shrink: 0;
`

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 14px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: #fafafa;
  }
`

const BackArrow = styled.span`
  font-size: 14px;
  color: #999;
  margin-right: 8px;
  cursor: pointer;
`

const DetailTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #222;
`

const DetailCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #999;
  margin-left: auto;
`

const EventItem = styled.div`
  padding: 6px 14px;
  font-size: 12px;
  color: #555;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`

const EventItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const PlateNo = styled.span`
  font-weight: 500;
  color: #333;
`

const EventTime = styled.span`
  color: #999;
  font-size: 11px;
`

const SiteName = styled.div`
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
`

const EventList = styled.div`
  max-height: 250px;
  overflow: auto;
`

const formatPreview = item => {
  if (!item) return ''
  const plate = item.plateNo || '-'
  const code = item.vehicleCode || '-'
  const time = item.startAt ? moment(item.startAt).format('HH:mm') : '-'
  return `${plate} - ${code} / ${time}`
}

const SafetyNotificationPopover = ({
  data,
  events,
  unreadByType,
  detailEvents,
  detailHasMore,
  detailPage,
  detailLoading,
  onLoadMore,
  onClose: _onClose,
  visible,
}) => {
  const [activeKey, setActiveKey] = useState(null)
  const loadingRef = useRef(false)
  const touchStartX = useRef(0)
  const touchRowKey = useRef(null)

  useEffect(() => {
    if (!visible) setActiveKey(null)
  }, [visible])

  const openDetail = key => {
    setActiveKey(key)
    if (onLoadMore) {
      onLoadMore(key, 1)
    }
  }

  const handleTouchStart = (e, rowKey) => {
    touchStartX.current = e.touches[0].clientX
    touchRowKey.current = rowKey
  }

  const handleTouchEnd = e => {
    const diff = touchStartX.current - e.changedTouches[0].clientX
    if (diff > SWIPE_THRESHOLD && touchRowKey.current !== null) {
      openDetail(touchRowKey.current)
    }
  }

  const handleDetailTouchStart = e => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleDetailTouchEnd = e => {
    const diff = e.changedTouches[0].clientX - touchStartX.current
    if (diff > SWIPE_THRESHOLD) {
      setActiveKey(null)
    }
  }

  const handleDetailScroll = e => {
    if (!activeKey || !onLoadMore) return
    const hasMore = detailHasMore && detailHasMore[activeKey]
    if (!hasMore || loadingRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadingRef.current = true
      const nextPage = ((detailPage && detailPage[activeKey]) || 0) + 1
      Promise.resolve(onLoadMore(activeKey, nextPage)).finally(() => {
        loadingRef.current = false
      })
    }
  }

  const activeEvent = activeKey
    ? EVENT_TYPES.find(et => et.key === activeKey)
    : null
  const activeEventList = useMemo(() => {
    const raw =
      activeKey && detailEvents && detailEvents[activeKey]
        ? detailEvents[activeKey]
        : []
    const seen = new Set()
    return raw.filter(item => {
      const uid = `${item.plateNo}_${item.startAt}`
      if (seen.has(uid)) return false
      seen.add(uid)
      return true
    })
  }, [activeKey, detailEvents])
  const hasMore = activeKey && detailHasMore && detailHasMore[activeKey]
  const isLoading = activeKey && detailLoading && detailLoading[activeKey]

  return (
    <LangContext.Consumer>
      {i18n => (
        <Container>
          <Title>{i18n.n.notification}</Title>
          <Divider style={{ margin: 0 }} />
          <SlideWrapper showDetail={activeKey !== null}>
            <ListView>
              {EVENT_TYPES.map(eventType => {
                const unread = unreadByType
                  ? unreadByType[eventType.key] || 0
                  : 0
                const eventList =
                  events && events[eventType.key] ? events[eventType.key] : []
                const latestItem = eventList[0] || null
                return (
                  <SwipeRow
                    key={eventType.key}
                    onClick={() => openDetail(eventType.key)}
                    onTouchStart={e => handleTouchStart(e, eventType.key)}
                    onTouchEnd={handleTouchEnd}
                  >
                    <RowContent>
                      <EventLabelRow>
                        {eventType.getLabel(i18n)}
                        {unread > 0 && <NewDot />}
                      </EventLabelRow>
                      <PreviewText>{formatPreview(latestItem)}</PreviewText>
                    </RowContent>
                    {unread > 0 && (
                      <BadgeWrapper>
                        <Badge
                          count={unread}
                          overflowCount={99}
                          style={{
                            backgroundColor: '#ff4d6a',
                            fontSize: 10,
                            minWidth: 18,
                            height: 18,
                            lineHeight: '18px',
                            borderRadius: 9,
                          }}
                        />
                      </BadgeWrapper>
                    )}
                  </SwipeRow>
                )
              })}
            </ListView>
            <DetailView
              onTouchStart={handleDetailTouchStart}
              onTouchEnd={handleDetailTouchEnd}
            >
              {activeEvent && (
                <>
                  <DetailHeader onClick={() => setActiveKey(null)}>
                    <BackArrow>&lsaquo;</BackArrow>
                    <DetailTitle>{activeEvent.getLabel(i18n)}</DetailTitle>
                    <DetailCount>
                      {data ? data[activeEvent.key] || 0 : 0}
                    </DetailCount>
                  </DetailHeader>
                  {isLoading && activeEventList.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 0' }}>
                      <Spin size="small" />
                    </div>
                  ) : activeEventList.length === 0 ? (
                    <Empty />
                  ) : (
                    <EventList onScroll={handleDetailScroll}>
                      {isLoading && (
                        <div style={{ textAlign: 'center', padding: '8px 0' }}>
                          <Spin size="small" />
                        </div>
                      )}
                      {activeEventList.map((item, idx) => (
                        <EventItem key={idx} isNew={item.isNew}>
                          <EventItemRow>
                            <PlateNo>
                              {item.plateNo || '-'}
                              {item.vehicleCode ? ` - ${item.vehicleCode}` : ''}
                              {item.isNew && <NewDot />}
                            </PlateNo>
                            <EventTime>
                              {item.avgValue
                                ? `${parseFloat(item.avgValue).toFixed(
                                    1,
                                  )} km/h · `
                                : ''}
                              {item.startAt
                                ? `${moment(item.startAt).format(
                                    'HH:mm',
                                  )}${!!item.endAt &&
                                    `- ${moment(item.endAt).format('HH:mm')}`}`
                                : '-'}
                            </EventTime>
                          </EventItemRow>
                          {item.siteName && (
                            <SiteName>{item.siteName}</SiteName>
                          )}
                        </EventItem>
                      ))}
                      {hasMore && (
                        <div style={{ textAlign: 'center', padding: '8px 0' }}>
                          <Spin size="small" />
                        </div>
                      )}
                    </EventList>
                  )}
                </>
              )}
            </DetailView>
          </SlideWrapper>
        </Container>
      )}
    </LangContext.Consumer>
  )
}

export default SafetyNotificationPopover
